import httpClient from '@shared/api/httpClient';
import { apiUrl } from '@configs/environment';
import { MyTeamEvalData } from '../interfaces/MyTeamEvalData';
import { FetchedMyPeopleEvalData } from './types';
import { evaluationRescheduleHistoryMapFunc } from '../helpers/mapFunctions/evaluationRescheduleHistoryMapFunc';
import getEvaluationPermissions from './helpers/getEvaluationPermissions';
import { MyPeopleEvalDataFilters } from '../pages/MyPeopleEvalPage/interfaces/MyPeopleEvalsDataFilters';
import { clientReviewerParser, peerReviewerParser } from './helpers/responseParsers';
import { SortDirection } from '@shared/enums/sortDirection';

export interface MyPeopleEvalDataResponse {
  data: MyTeamEvalData[];
  total: number;
}

export const fetchMyPeopleEvalData = (payload: MyPeopleEvalDataFilters): Promise<MyPeopleEvalDataResponse> =>
  httpClient
    .get<FetchedMyPeopleEvalData>(`${apiUrl}/evaluations/my-team`, {
      params: {
        limit: payload.limit,
        offset: payload.offset,
        status: payload.status?.map((s) => s.toLowerCase()).join(','),
        locations: payload.locations?.join(','),
        manager_ids: payload.managers?.join(','),
        member_ids: payload.members?.join(','),
        date_from: payload.dateFrom,
        date_to: payload.dateTo,
        department_ids: payload.departments?.join(','),
        include_regular: payload.includeRegular,
        include_eop: payload.includeEop,
        eop_status: payload.eopStatus?.toLowerCase(),
        sort_by: payload.sortInfo?.field,
        sort_order: payload.sortInfo?.sortDirection === SortDirection.Asc ? 'asc' : 'desc',
        eop_resolved_at_from: payload.eopResolvedAtFrom,
        eop_resolved_at_to: payload.eopResolvedAtTo,
        eop_extended_to_from: payload.eopExtendedToFrom,
        eop_extended_to_to: payload.eopExtendedToTo,
        employment_types: payload.employmentType,
        has_ongoing_flag: payload.hasOngoingFlag || undefined,
        engagement_models: payload.engagementModels?.join(','),
      },
    })
    .then((resp) => {
      const data = resp.data.data.data
        .filter((user) => user.primary_reports_to)
        .map(
          ({
            imageUrl,
            email,
            full_name,
            status,
            evaluation_id,
            evaluation_date,
            position,
            id,
            is_submitted_by_evaluator,
            is_submitted_by_evaluatee,
            google_calendar_event,
            primary_reports_to,
            country,
            city,
            office_country,
            is_primary_reporter,
            evaluation_reschedulings,
            type_of_interaction,
            peer_reviewers,
            client_reviewers,
            meta: { permissions },
            has_feedbacks,
            is_extendable,
            eop_resolved_at,
            eop_extended_to,
            eop_status,
            eop_not_passed_reason,
            eop_extended_reason,
            department,
            projects,
          }) => ({
            email,
            name: full_name,
            userId: id,
            evaluationId: evaluation_id,
            imageUrl,
            position,
            status,
            evaluationDate: evaluation_date,
            isSubmittedByEvaluator: is_submitted_by_evaluator,
            isSubmittedByEvaluatee: is_submitted_by_evaluatee,
            googleCalendarEvent: google_calendar_event
              ? {
                  ...google_calendar_event,
                  attendees: google_calendar_event.attendees.map((attendee) => {
                    return {
                      name: attendee.full_name,
                      email: attendee.email,
                      id: attendee.id,
                      imageUrl: attendee.imageUrl,
                      position: attendee.position,
                      city: attendee.city,
                      country: attendee.country,
                    };
                  }),
                }
              : null,
            primaryReportsTo: {
              id: primary_reports_to.id,
              email: primary_reports_to.email,
              name: primary_reports_to.full_name,
              imageUrl: primary_reports_to.imageUrl,
            },
            country: country,
            city: city,
            officeCountry: office_country,
            peerReviews: peer_reviewers?.map(peerReviewerParser) || null,
            clientReviews: client_reviewers?.map(clientReviewerParser) || null,
            isPrimaryReporter: is_primary_reporter,
            permissions: getEvaluationPermissions(permissions).evaluation,
            evaluationRescheduleHistory: evaluation_reschedulings
              ? evaluationRescheduleHistoryMapFunc(evaluation_reschedulings)
              : [],
            department: department,
            project: projects?.join(', '),
            typeOfInteraction: type_of_interaction,
            hasFeedbacks: has_feedbacks,
            isExtendable: is_extendable,
            eopResolvedAt: eop_resolved_at,
            eopExtendedTo: eop_extended_to,
            eopStatus: eop_status,
            eopNotPassedReason: eop_not_passed_reason,
            eopExtendedReason: eop_extended_reason,
          })
        );
      return { data, total: resp.data.data.total ?? 0 };
    });
